import { useMutation, useQuery } from "@apollo/client";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { Link } from "react-router-dom";

import {
  GridInputs,
  multiSelect,
  Table,
} from "../../../../../../../components/Functional";
import { Button, Modal } from "../../../../../../../components/ui";
import { CREATE_CONSIGNMENT_WORKS as createConsignmentWorksMutation } from "../../../../../../../graphql/mutation/ConsignmentWork";
import { FETCH_ARTISTS as fetchArtists } from "../../../../../../../graphql/query/Artist";
import { FETCH_INSTANCES_V2 as fetchInstancesV2 } from "../../../../../../../graphql/query/Instance";
import { useCompanies } from "../../../../../../../hooks/useCompanies";
import useUser from "../../../../../../../hooks/useUser";
import { convertToFilter } from "../../../../../../../util";
import { ValidWorkStatus } from "./ValidWorkStatus";

const FilterBar = (props = {}) => {
  const { formik } = props;
  const { data: { artists = [] } = {} } = useQuery(fetchArtists());
  const { companies } = useCompanies();

  const inputs = {
    className: "grid-cols-1 gap-4 lg:grid-cols-5",
    inputs: [
      {
        label: "Company",
        name: "companyId",
        type: "multi-select",
        options: companies?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
      { label: "Control Reference", name: "ref", type: "text" },
      {
        label: "Artist",
        name: "_edition._artwork._artist.id",
        type: "multi-select",
        options: artists?.map(item => ({ label: item?.name, value: item?.id })),
      },
      { label: "Title", name: "_edition._artwork.title", type: "text" },
      {
        label: "Edition #",
        name: "editionNumber",
        type: "number",
        allowEmpty: true,
      },
      { label: "Name", name: "name", type: "text" },
      { label: "Artist Ref", name: "artistRef", type: "text" },
      { label: "Series", name: "_edition._artwork.series", type: "text" },
      {
        designType: "threeStateCheckbox",
        label: "Photographed",
        name: "_edition._artwork.photographed",
        type: "toggle",
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  return <GridInputs {...inputProps} />;
};

const AddWorkModal = props => {
  const { closeModal, consignment } = props;
  const [warnings, setWarnings] = useState([]);
  const [consignments, setConsignments] = useState([]);
  const [createConsignmentWorks, { loading, error, reset }] = useMutation(
    createConsignmentWorksMutation({
      artistNames: false,
      consignment: false,
      instance: false,
      user: false,
    }),
    { refetchQueries: ["FetchConsignmentWorks", "FetchConsignment"] },
  );

  const formik = useFormik({
    initialValues: {},
    onSubmit: async values => {
      const { selected: { ids = {} } = {} } = values;
      const instanceIds = Object.keys(ids).map(key =>
        parseInt(key.replace("_", ""), 10),
      );

      const response = await createConsignmentWorks({
        variables: {
          input: {
            consignmentId: consignment?.id,
            instanceIds,
          },
        },
      });

      if (response?.data?.createConsignmentWorks?.success) {
        const warnings = response.data.createConsignmentWorks.warnings;
        const consignmentWorks =
          response.data.createConsignmentWorks.consignmentWorks;

        setConsignments(() => consignmentWorks);
        setWarnings(() => warnings);
      } else {
        resetAll();
      }
    },
  });

  const filterFormik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({
      editionNumber: Yup.number()
        .nullable()
        .integer()
        .positive()
        .typeError("Invalid input"),
    }),
  });

  const EditionNumber = ({ values }) => {
    if (!values?.editionNumberFormatted) return null;
    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">
          {values?.editionNumberFormatted}
        </span>
      </div>
    );
  };

  const resetAll = () => {
    formik.resetForm();
    reset();
    setConsignments([]);
    setWarnings([]);
    closeModal();
  };

  const tableProps = {
    debounceDelay: 800,
    allowHorizontalScroll: true,
    dataKey: "instancesV2",
    FETCH_QUERY: fetchInstancesV2({
      name: true,
      editionNumberFormatted: true,
      edition: {
        number: true,
        year: true,
        artwork: {
          title: true,
          artists: {
            name: true,
          },
          images: {
            imgT: true,
          },
        },
      },
      status: {
        name: true,
      },
    }),
    headers: [
      multiSelect({
        formik,
        label: "",
        name: "selected",
        keyName: "id",
        showAll: false,
      }),
      {
        label: "Artwork Image",
        name: "edition.artwork.images[0].imgT",
        type: "image",
      },
      { label: "Reference", name: "name", type: "label" },
      {
        label: "Title",
        name: "edition.artwork.title",
        type: "label",
        widthInPx: 400,
      },
      {
        label: "Artist Name",
        name: "edition.artwork.artists.[0].name",
        type: "label",
      },
      { label: "Year", name: "edition.year", type: "label" },
      {
        label: "Edition",
        name: "editionNumberFormatted",
        type: "custom",
        component: EditionNumber,
      },
      { label: "Status", name: "status.name", type: "label" },
    ],
    inputs: [],
    limit: 10,
    skip: () => !filterFormik?.isValid,
    variables: {
      statusId: {
        operator: "in",
        value: ValidWorkStatus(consignment),
      },
      ...convertToFilter({
        values: filterFormik?.values,
        skipKeys: ["limit", "offset"],
      }),
    },
    pagination: {
      variant: "sm",
    },
    variant: "dynamicHeight",
  };

  // API call succeeded
  if (consignments.length || warnings.length) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <XMarkIcon
          className={`text-black-400 absolute right-3 top-3 h-4 w-4 cursor-pointer`}
          onClick={() => {
            resetAll();
          }}
        />
        <div className="flex flex-col">
          <div className="m-2 text-2xl font-bold">
            Added {consignments.length} out of{" "}
            {Object.keys(formik.values.selected?.ids ?? {}).length} works
          </div>
          {consignments.length ? (
            <div className="flex flex-wrap">
              {consignments.map((consignment, index) => {
                const { instanceId } = consignment;

                return (
                  <div key={index} className="py-2">
                    <Link
                      className="inline px-1 underline"
                      to={`/instance/${instanceId}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {instanceId}
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <hr className="my-3" />
        {warnings.length ? (
          <div className="flex flex-col">
            <div className="m-2 text-2xl font-bold">Warnings</div>
            <div className="flex flex-col">
              {warnings.map((warning, index) => {
                const { message, instanceId } = warning;

                return (
                  <div key={index} className="py-2">
                    <Link
                      className="underline"
                      to={`/instance/${instanceId}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {instanceId}
                    </Link>
                    : <span>{message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col">
            <div className="text-2xl font-bold">Error adding works</div>
          </div>
          <div className="flex flex-col">
            <div>
              <Button
                action="default"
                className="mr-4"
                label="Cancel"
                onClick={() => {
                  resetAll();
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="py-6">{error?.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="work-error mt-6 flex w-full flex-col">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Add Works: Select Instances</div>
        </div>
        <div className="mb-5 flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              onClick={() => {
                resetAll();
              }}
            />
            <Button
              label={loading ? "Adding" : "Add"}
              disabled={
                loading ||
                !Object.keys(formik.values.selected?.ids ?? {}).length
              }
              onClick={formik?.submitForm}
            />
          </div>
        </div>
      </div>
      <FilterBar formik={filterFormik} />
      <Table {...tableProps} />
    </div>
  );
};

const AddWork = props => {
  const { hasPermission } = useUser();
  const { consignment } = props || {};

  const modalProps = {
    body: AddWorkModal,
    closeOnBackdrop: false,
    consignment,
    hideCloseButton: true,
    scale: "md",
    disabled: !hasPermission("ADD_CONSIGNMENT_ITEM"),
  };

  return (
    <Modal {...modalProps}>
      <Button
        label="Add Works"
        action="black"
        disabled={!hasPermission("ADD_CONSIGNMENT_ITEM")}
      />
    </Modal>
  );
};

export default AddWork;
